import { Layout } from 'layouts/Layout'
import { IconSvg } from '@prism/icon'
import { shallow } from 'zustand/shallow'
import { StyledHeaderActions, StyledStickyHeaderPage } from 'modules/VehicleList'
import { useFetch } from 'hooks/useFetch'
import { purchaseHistorySortOptions } from 'common/constants'
import { VehicleStickyBar } from 'modules/VehicleList/VehicleStickyBar'
import { PurchaseHistoryContext, PurchaseHistoryProvider } from 'contexts/PurchaseHistoryContext'
import { ToastContextProvider } from 'contexts/ToastContext'
import { useContext, useCallback, useEffect, useState } from 'react'
import { SearchSection } from 'modules/PurchaseHistory/SearchSection'
import { Loader, OverlayLoader } from 'components/Loader'
import { PurchaseHistoryListSection } from 'modules/PurchaseHistory/PurchaseHistoryListSection'
import { Link } from 'components/Share/Link'
import { formatDateString } from 'utils/dateUtils'
import { getManheimCheckoutURL } from 'apis/userApis'
import { Sort } from 'components/Sort/Sort'
import { ISortBy } from 'types/baseTypes'
import { setListingBreadcrumb } from 'utils/breadcrumbUtils'
import { useGlobalStore } from 'store/useGlobalStore'
import { useLocation } from 'react-router-dom'
import { Rules } from 'common/rules'
import { ArbitrationState, IArbitrationRequest } from '../types/arbitrationTypes'
import { ArbitrationForm } from '../modules/PurchaseHistory/ArbitrationForm'
import { StyledArbitrationModal } from '../modules/PurchaseHistory/ArbitrationForm.styled'

export const PurchaseHistoryContent = () => {
  const { loading, result, searchFilter, updateSearchFilter, handleSearch } = useContext(PurchaseHistoryContext)
  const getLocalText = useGlobalStore((state) => state.getLocalText)
  const location = useLocation()
  const [isInRule] = useGlobalStore((state) => [state.isInRule], shallow)

  useEffect(() => {
    const pageTitle = getLocalText('PURCHASE_HISTORY', 'Purchase History')
    setListingBreadcrumb(pageTitle, location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getDownloadReportUrl = () => {
    var startDateValue = formatDateString(new Date(searchFilter?.FromDate))
    var endDateValue = formatDateString(new Date(searchFilter?.ToDate))
    var downloadReportUrl = `/resource/DownloadPurchaseHistory?startDate=${startDateValue}&endDate=${endDateValue}&sortBy=${searchFilter.SortBy.Value}&sortOrder=${searchFilter.SortBy.SortOrder}`
    return downloadReportUrl
  }

  const handleSortChange = useCallback(
    (selectedSortBy: ISortBy) => {
      updateSearchFilter({ ...searchFilter, SortBy: selectedSortBy })
    },
    [searchFilter, updateSearchFilter]
  )

  const { loading: manHeimLoading, data: manHeimLink } = useFetch(getManheimCheckoutURL)

  const [showArbitrationModal, setShowArbitrationModal] = useState(false)
  const [selectedArbitration, setSelectedArbitration] = useState<IArbitrationRequest>(null)
  const [isUpdateAttachment, setIsUpdateAttachment] = useState(false)
  const [isPageLoading, setIsPageLoading] = useState(false)

  const openArbitrationModal = (request: IArbitrationRequest, updateAttachment = false) => {
    setSelectedArbitration(request)
    setIsUpdateAttachment(updateAttachment)
    setShowArbitrationModal(true)
  }

  const closeArbitrationModal = () => setShowArbitrationModal(false)

  const handleAddAttachmentsClick = (req: IArbitrationRequest) => openArbitrationModal(req, true)

  const handleArbitrationComplete = (_state: ArbitrationState) => {
    setShowArbitrationModal(false)
    setSelectedArbitration(null)
    if (handleSearch) {
      setTimeout(() => handleSearch(false), 5000)
    }
  }

  return (
    <Layout title={'Purchase History'}>
      {(loading || isPageLoading) && <OverlayLoader />}
      <StyledStickyHeaderPage>
        <VehicleStickyBar
          sectionTitle={'Purchase History'}
          itemCount={result?.TotalRecords ? result.TotalRecords : 0}
          displayCollapse={false}
          rightActionSpan={8}
          rightActions={
            <StyledHeaderActions>
              <div className="view-icons">
                <Link to={getDownloadReportUrl()}>
                  <span className="right-action">
                    <IconSvg glyph="download" color="primary" />
                    &nbsp;Download Report&nbsp;
                  </span>
                </Link>
                {isInRule(Rules.VIEW_CHECKOUT_AT_MANHEIMCOM) && (
                  <>
                    {manHeimLoading ? (
                      <Loader />
                    ) : (
                      <Link to={manHeimLink} target="_blank">
                        <span className="right-action">
                          <IconSvg glyph="link" color="primary" />
                          &nbsp;Check out at Manheim&nbsp;
                        </span>
                      </Link>
                    )}
                  </>
                )}
              </div>
              <Sort
                currentSort={searchFilter.SortBy}
                sortOptions={purchaseHistorySortOptions}
                onChange={handleSortChange}
              />
            </StyledHeaderActions>
          }
        />
        <SearchSection />
        <PurchaseHistoryListSection
          onArbitrationClick={openArbitrationModal}
          onAddAttachmentsClick={handleAddAttachmentsClick}
        />
      </StyledStickyHeaderPage>
      {showArbitrationModal && selectedArbitration && (
        <StyledArbitrationModal
          show={showArbitrationModal}
          onHide={closeArbitrationModal}
          backdrop="static"
          keyboard={false}
          size="lg"
          centered
        >
          <ArbitrationForm
            vehicleInstanceID={selectedArbitration.vehicleInstanceID}
            buyerVehiclePurchaseID={selectedArbitration.buyerVehiclePurchaseID}
            onClose={closeArbitrationModal}
            isUpdateAttachment={isUpdateAttachment}
            onArbitrationComplete={handleArbitrationComplete}
            setIsPageLoading={setIsPageLoading}
          />
        </StyledArbitrationModal>
      )}
    </Layout>
  )
}

export const PurchaseHistory = () => {
  return (
    <PurchaseHistoryProvider>
      <ToastContextProvider>
        <PurchaseHistoryContent />
      </ToastContextProvider>
    </PurchaseHistoryProvider>
  )
}
